/**
 * Auth store.
 *
 * Zustand holds UI/session state. Live prices do NOT go here — they belong in
 * the tick store (Phase 3), outside React entirely. Putting 330 live values
 * through a React store is the single biggest frontend performance trap in
 * this product (ARCHITECTURE.md §4.7).
 */

import type { TerminalTheme } from '@apna/shared'
import { create } from 'zustand'
import type { AuthResponse, AuthUser } from '@apna/shared'
import { api, refreshSession, setAccessToken, setAuthLostHandler } from '@/lib/api'

type Status = 'booting' | 'authenticated' | 'anonymous'

interface AuthState {
  status: Status
  user: AuthUser | null
  error: string | null

  /** Called once on app mount. Attempts a silent refresh from the cookie. */
  boot: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name?: string) => Promise<void>
  logout: () => Promise<void>
  setTheme: (theme: TerminalTheme) => Promise<void>
  clearError: () => void
}

/**
 * Apply the theme to <html>.
 *
 * The class is set in index.html so the first paint is already dark; this
 * reconciles it with the user's stored preference once we know who they are.
 */
function applyTheme(theme: TerminalTheme): void {
  const root = document.documentElement
  /*
   * Tailwind's `dark:` variant keys off this class, and `bloomberg` is a
   * dark theme for that purpose even though it is not `dark` — otherwise
   * every `dark:` utility in the app would render light type on black.
   */
  root.classList.toggle('dark', theme === 'dark' || theme === 'bloomberg')
  // The theme also keys off `data-theme` so a future light/dark toggle can
  // override the media query without a class-order fight.
  root.dataset.theme = theme
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'booting',
  user: null,
  error: null,

  boot: async () => {
    // Clearing app state when a refresh fails has to live here rather than in
    // the API client, which has no business knowing about the store.
    setAuthLostHandler(() => set({ status: 'anonymous', user: null }))

    const ok = await refreshSession()
    if (!ok) {
      set({ status: 'anonymous', user: null })
      return
    }
    try {
      const user = await api.get<AuthUser>('/auth/me')
      applyTheme(user.theme)
      set({ status: 'authenticated', user, error: null })
    } catch {
      set({ status: 'anonymous', user: null })
    }
  },

  login: async (email, password) => {
    set({ error: null })
    try {
      const res = await api.post<AuthResponse>('/auth/login', { email, password })
      setAccessToken(res.accessToken)
      applyTheme(res.user.theme)
      set({ status: 'authenticated', user: res.user, error: null })
    } catch (err) {
      set({ error: (err as Error).message })
      throw err
    }
  },

  register: async (email, password, name) => {
    set({ error: null })
    try {
      const res = await api.post<AuthResponse>('/auth/register', { email, password, name })
      setAccessToken(res.accessToken)
      applyTheme(res.user.theme)
      set({ status: 'authenticated', user: res.user, error: null })
    } catch (err) {
      set({ error: (err as Error).message })
      throw err
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      // Local state is cleared even if the server call fails — a user who
      // clicked "sign out" must not be left looking at a live terminal.
      setAccessToken(null)
      set({ status: 'anonymous', user: null, error: null })
    }
  },

  setTheme: async (theme) => {
    applyTheme(theme)
    const prev = get().user
    if (prev) set({ user: { ...prev, theme } })
    try {
      const user = await api.patch<AuthUser>('/auth/me/theme', { theme })
      set({ user })
    } catch {
      // Keep the optimistic local change: a failed preference write is not
      // worth yanking the theme back out from under the user.
    }
  },

  clearError: () => set({ error: null }),
}))
